/**
 * Licence gate. Public repositories are free. Reviewing (or posting to) a PRIVATE repository needs a
 * sibyl licence ($49 a year per organisation, bought on Gumroad). Nothing else differs between the
 * tiers: the engine, the gates and the console are the same code.
 *
 * Key: SIBYL_LICENSE_KEY or LICENSE_KEY. Cached in ~/.config/sibyl/licence.json
 * (SIBYL_LICENCE_FILE overrides), re-checked every 24 h, kept 7 days if Gumroad is unreachable.
 * Under GitHub Actions nothing persists between runs, so the key is checked without counting an
 * activation. The check is vendor/gumroad-licence.mjs (gumroad-licence 1.2.0), unmodified.
 *
 * ⚠️ When GitHub will not say whether the repository is private (an API error), the run is NOT
 * refused here: the review's own GitHub calls fail with the real reason a moment later, and a
 * licence message on top of a network fault would point the user at the wrong problem.
 */
import { homedir } from "node:os"
import { join } from "node:path"

export const PRODUCT = {
  // Gumroad product "sibyl" — research sends the id; until then the permalink is used.
  productId: "REPLACE-GUMROAD-PRODUCT-ID",
  permalink: "sibyl",
  seller: "tanglement",
}
export const BUY_URL = "https://tanglement.gumroad.com/l/sibyl"

export interface LicenceStatus {
  readonly unlocked: boolean
  readonly state: string
  readonly message?: string
  readonly variants?: string
}

type Env = Record<string, string | undefined>
interface Deps {
  readonly fetchFn?: typeof fetch
  readonly now?: () => number
}

const VENDOR = new URL("../../vendor/", import.meta.url)

export async function licenceStatus(env: Env = process.env, deps: Deps = {}): Promise<LicenceStatus> {
  const key = env["SIBYL_LICENSE_KEY"] || env["LICENSE_KEY"] || ""
  if (key === "") return { unlocked: false, state: "none", message: "No licence key set (free tier: public repositories)." }
  const { createGumroadLicence } = (await import(new URL("gumroad-licence.mjs", VENDOR).href)) as {
    createGumroadLicence: (cfg: typeof PRODUCT, o: object) => { ensure: (k: string, force: boolean, o: { countUse: boolean }) => Promise<LicenceStatus> }
  }
  const { fileStorage } = (await import(new URL("file-storage.mjs", VENDOR).href)) as { fileStorage: (f: string) => object }
  const file = env["SIBYL_LICENCE_FILE"] || join(env["HOME"] || homedir(), ".config", "sibyl", "licence.json")
  const lic = createGumroadLicence(PRODUCT, {
    storage: fileStorage(file),
    ...(deps.fetchFn !== undefined ? { fetchFn: deps.fetchFn } : {}),
    ...(deps.now !== undefined ? { now: deps.now } : {}),
  })
  return lic.ensure(key, false, { countUse: env["GITHUB_ACTIONS"] !== "true" })
}

export type Visibility = "public" | "private" | "unknown"

export async function repoVisibility(repo: string, token: string, fetchFn: typeof fetch = fetch): Promise<Visibility> {
  try {
    const res = await fetchFn(`https://api.github.com/repos/${repo}`, {
      headers: { authorization: `Bearer ${token}`, accept: "application/vnd.github+json", "user-agent": "sibyl" },
    })
    if (!res.ok) return "unknown"
    const d = (await res.json()) as { private?: unknown }
    return d.private === true ? "private" : d.private === false ? "public" : "unknown"
  } catch {
    return "unknown"
  }
}

/** Undefined when the run may go ahead; otherwise the message to print (and the run stops). */
export async function licenceRefusal(
  repo: string,
  token: string,
  env: Env = process.env,
  deps: Deps = {},
): Promise<string | undefined> {
  const vis = await repoVisibility(repo, token, deps.fetchFn)
  if (vis !== "private") return undefined
  const lic = await licenceStatus(env, deps)
  if (lic.unlocked) return undefined
  return [
    `⛔ ${repo} is a private repository, and reviewing private repositories needs a sibyl licence.`,
    `   ${formatStatus(lic)}`,
    `   Public repositories are free. Licence ($49/yr per organisation): ${BUY_URL} — then set SIBYL_LICENSE_KEY.`,
  ].join("\n")
}

export function formatStatus(s: LicenceStatus): string {
  const tier = s.unlocked ? `licensed${s.variants ? ` (${s.variants})` : ""}` : "free tier"
  return `sibyl licence: ${tier} — state ${s.state}${s.message ? `\n   ${s.message}` : ""}`
}
